import { GlobalSearchView, SearchResultGroup } from '../globalSearch.types';
import { StaticCommand } from '../staticCommands/staticCommands.types';
import useDelayedFlag from './useDelayedFlag';

const DIM_RESULTS_DELAY_MS = 700;
const LOADING_GRACE_MS = 150;
const LOADING_MIN_HOLD_MS = 300;

interface ResolveViewParams {
  hasReachedMinLength: boolean;
  isError: boolean;
  isLoading: boolean;
  hasData: boolean;
  isEmptyResult: boolean;
}

const resolveView = ({
  hasReachedMinLength,
  isError,
  isLoading,
  hasData,
  isEmptyResult,
}: ResolveViewParams): GlobalSearchView => {
  if (!hasReachedMinLength) {
    return 'empty';
  }
  if (isError && !hasData) {
    return 'error';
  }
  if (isLoading) {
    return 'loading';
  }
  if (isEmptyResult) {
    return 'no-result';
  }
  return 'results';
};

interface UseGlobalSearchViewStateParams {
  hasReachedMinLength: boolean;
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  hasData: boolean;
  groups: SearchResultGroup[];
  staticCommands: StaticCommand[];
}

const useGlobalSearchViewState = ({
  hasReachedMinLength,
  isError,
  isLoading,
  isFetching,
  hasData,
  groups,
  staticCommands,
}: UseGlobalSearchViewStateParams) => {
  const isRefetching = isFetching && hasData;
  const showDimmedResults = useDelayedFlag({ value: isRefetching, delayMs: DIM_RESULTS_DELAY_MS });

  const isSearchLoading = useDelayedFlag({
    value: isFetching,
    delayMs: LOADING_GRACE_MS,
    minHoldMs: LOADING_MIN_HOLD_MS,
  });
  const isDelayedLoading = useDelayedFlag({
    value: isLoading,
    delayMs: LOADING_GRACE_MS,
    minHoldMs: LOADING_MIN_HOLD_MS,
  });

  const hasNoEntityResults = groups.every((group) => group.items.length === 0);
  const isEmptyResult = hasData && hasNoEntityResults && staticCommands.length === 0;

  const view = resolveView({
    hasReachedMinLength,
    isError,
    isLoading: isDelayedLoading,
    hasData,
    isEmptyResult,
  });

  const commands = view === 'results' ? staticCommands : [];

  return { view, isSearchLoading, showDimmedResults, commands };
};

export default useGlobalSearchViewState;
